import React from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { Section, SectionHeading } from '../components/ui/Section';
import { Prose, ProseParagraph, ProseList } from '../components/ui/Prose';
import { PullQuote, SectionDivider } from '../components/ui/PullQuote';
import { ContinueExploring } from '../components/ui/ContinueExploring';

const ForInvestors = () => {
  return (
    <div>
      <PageHeader 
        title="For Investors"
        subtitle="Investing in the ability to choose the world our systems create"
      />

      {/* A capability humanity has never had */}
      <Section>
        <SectionHeading>A capability humanity has never had</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Humanity has built powerful systems for centuries. Economic systems. Technological systems. Political systems. Organizational systems.
          </ProseParagraph>
          
          <ProseParagraph>
            What humanity has never had—until now—is a reliable way to govern what those systems ultimately produce once they scale.
          </ProseParagraph>
          
          <ProseParagraph>
            Instead, outcomes have been shaped indirectly:
          </ProseParagraph>
          
          <ProseList items={[
            "by incentives",
            "by defaults",
            "by short-term optimization",
            "by unintended consequences"
          ]} />
          
          <ProseParagraph>
            Even well-intentioned systems drift. Not because people fail—but because direction itself was never governed.
          </ProseParagraph>
          
          <ProseParagraph>
            Coherence OS exists to change that.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What has quietly become possible */}
      <Section>
        <SectionHeading>What has quietly become possible</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            For the first time, it is now possible to design systems so that:
          </ProseParagraph>
          
          <ProseList items={[
            "speed does not overpower discernment",
            "scale does not erode dignity",
            "success does not consume the future it depends on"
          ]} />
          
          <ProseParagraph>
            Not through policy. Not through values statements. Not through after-the-fact correction.
          </ProseParagraph>
          
          <ProseParagraph>
            But through <strong>structure</strong>.
          </ProseParagraph>
          
          <ProseParagraph>
            Coherence OS introduces a new operating layer that allows builders to decide—explicitly and durably—what kind of world their systems are building as the default.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What investing in Coherence OS actually means */}
      <Section>
        <SectionHeading>What investing in Coherence OS actually means</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Investing in Coherence OS is not about backing a single product, feature, or company.
          </ProseParagraph>
          
          <ProseParagraph>
            It is about participating in the creation of a foundational operating layer for civilization-scale systems.
          </ProseParagraph>
          
          <ProseParagraph>
            This layer enables companies, organizations, platforms, and ecosystems to:
          </ProseParagraph>
          
          <ProseList items={[
            "pursue ambitious goals without drifting away from human dignity",
            "scale innovation without fragmenting trust, meaning, or coherence",
            "move quickly while remaining aligned to long-term civilizational outcomes"
          ]} />
          
          <ProseParagraph>
            Not because people behave differently—but because the system itself is oriented correctly.
          </ProseParagraph>
          
          <ProseParagraph>
            In simple terms, Coherence OS makes it possible for progress to add to the future rather than borrow from it.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Why this matters now */}
      <Section>
        <SectionHeading>Why this matters now</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Three shifts have converged:
          </ProseParagraph>
          
          <div className="space-y-6 mt-6">
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Acceleration</h4>
              <ProseParagraph className="!mb-0">
                AI and automation dramatically increase speed and reach.
              </ProseParagraph>
            </div>
            
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Scale</h4>
              <ProseParagraph className="!mb-0">
                Systems now shape lives, economies, and cultures globally.
              </ProseParagraph>
            </div>
            
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Irreversibility</h4>
              <ProseParagraph className="!mb-0">
                Once certain outcomes are locked in, correction becomes impossible.
              </ProseParagraph>
            </div>
          </div>
          
          <ProseParagraph className="mt-8">
            Together, these shifts mean that direction can no longer be left implicit.
          </ProseParagraph>
          
          <ProseParagraph>
            When systems gain the power to shape civilization by default, the ability to govern what they are shaping toward becomes essential infrastructure.
          </ProseParagraph>
          
          <ProseParagraph>
            Coherence OS exists because that moment has arrived.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* This is not ESG, ethics, or compliance */}
      <Section>
        <SectionHeading>This is not ESG, ethics, or compliance</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Many familiar approaches attempt to influence outcomes:
          </ProseParagraph>
          
          <ProseList items={[
            "ESG reporting",
            "impact measurement",
            "ethics frameworks",
            "governance committees"
          ]} />
          
          <ProseParagraph>
            These approaches operate <strong>around</strong> systems.
          </ProseParagraph>
          
          <ProseParagraph>
            Coherence OS operates <strong>within</strong> them.
          </ProseParagraph>
          
          <ProseParagraph>
            It governs:
          </ProseParagraph>
          
          <ProseList items={[
            "how success is defined",
            "which tradeoffs are permitted",
            'what forms of "winning" are structurally impossible'
          ]} />
          
          <ProseParagraph>
            This is not about moral positioning. It is about designing systems that remain coherent as they become powerful.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What kind of investment this is */}
      <Section>
        <SectionHeading>What kind of investment this is</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is not designed to exploit a temporary market gap. It is designed to become more valuable as complexity increases.
          </ProseParagraph>
          
          <ProseParagraph>
            As systems grow larger and more autonomous:
          </ProseParagraph>
          
          <ProseList items={[
            "unintended consequences multiply",
            "drift becomes harder to detect",
            "and the cost of misalignment rises"
          ]} />
          
          <ProseParagraph>
            Coherence OS functions as a:
          </ProseParagraph>
          
          <ProseList items={[
            "direction-setting layer",
            "coherence-preserving infrastructure",
            "category-defining operating system for long-horizon value creation"
          ]} />
          
          <ProseParagraph>
            This is an investment in a capability humanity will increasingly depend on.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Why this creates defensibility */}
      <Section>
        <SectionHeading>Why this creates defensibility</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is defensible because:
          </ProseParagraph>
          
          <ProseList items={[
            "it defines a category rather than competing within one",
            "it operates at a governing layer most systems lack",
            "it becomes harder to remove as reliance grows",
            "it is protected by filed and pending patents"
          ]} />
          
          <ProseParagraph>
            As acceleration continues, this layer does not become optional. It becomes foundational.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Who this is aligned with */}
      <Section>
        <SectionHeading>Who this is aligned with</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is aligned with investors who:
          </ProseParagraph>
          
          <ProseList items={[
            "think beyond quarters and cycles",
            "understand that capital shapes civilization through systems",
            "are drawn to category-defining infrastructure",
            "want to participate in building a future that is both ambitious and coherent"
          ]} />
          
          <ProseParagraph>
            If you are asking:
          </ProseParagraph>
          
          <ProseParagraph className="font-serif italic">
            What becomes possible when the builders of our most powerful systems can finally choose—explicitly and structurally—the world their success creates?
          </ProseParagraph>
          
          <ProseParagraph>
            You are already engaging the question Coherence OS was built to answer.
          </ProseParagraph>
        </Prose>
        
        {/* Continue exploring */}
        <ContinueExploring 
          links={[
            { label: 'The Category', href: '/the-category' },
            { label: 'Coherence Metrics', href: '/coherence-metrics' },
            { label: 'For Builders', href: '/for-builders' },
          ]}
        />
      </Section>
    </div>
  );
};

export default ForInvestors;
